import { useEffect, useRef, type RefObject } from 'react';

/** Half of the element on screen … */
export const IMPRESSION_THRESHOLD = 0.5;
/** … for a full second, uninterrupted. */
export const IMPRESSION_DWELL_MS = 1_000;

/**
 * Calls `onSeen` once, when the element has been at least half visible for a
 * second. A tip rendered below the fold of a long hub, or scrolled past in a
 * blink, has not been seen and does not count against the day's impressions.
 *
 * Where `IntersectionObserver` does not exist (old engines, jsdom), an element
 * that stays mounted for the dwell time counts: an impression slightly
 * over-counted is better than a tip whose click-through rate is undefined.
 */
export function useImpression<T extends Element>(
  ref: RefObject<T | null>,
  onSeen: () => void,
  enabled = true,
): void {
  const callback = useRef(onSeen);
  useEffect(() => {
    callback.current = onSeen;
  });

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let done = false;
    const fire = (): void => {
      if (done) return;
      done = true;
      callback.current();
    };

    if (typeof IntersectionObserver === 'undefined') {
      timer = setTimeout(fire, IMPRESSION_DWELL_MS);
      return () => clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_THRESHOLD) {
          if (timer === undefined) {
            timer = setTimeout(() => {
              fire();
              observer.disconnect();
            }, IMPRESSION_DWELL_MS);
          }
        } else if (timer !== undefined) {
          clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold: [0, IMPRESSION_THRESHOLD, 1] },
    );
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [ref, enabled]);
}
